// Timezone-aware calendar/date helpers — the single source of truth for every
// user-facing date rendering and day-distance in the app (PR-2,
// S2-DEADLINES-DOCS-01 / s6-forms-milestones-01).
//
// Two value families flow through here, and mixing them is exactly the bug
// this module fixes:
//   1. DATE-SEMANTIC values — Postgres `date` columns ('YYYY-MM-DD' via
//      PostgREST) and the date-semantic timestamptz task due_dates (writers
//      store plain dates; Postgres records UTC midnight). These are calendar
//      dates, not instants: render their stored parts (formatDateOnly /
//      formatDueDate) and measure distance as CALENDAR-DATE distance in the
//      user's resolved zone (daysUntil).
//   2. INSTANTS — real timestamptz moments (created_at, sent_at, …): render
//      with formatInstant* in the user's resolved zone.
//
// The old shared daysUntil (Math.ceil((utcMidnightTarget - wallClockNow)/day))
// under-counted by one day the moment UTC crossed midnight (~18:00 in Boise),
// flipping tomorrow-due deadlines to OVERDUE for ~25% of every day.
//
// Resolution chain for the display zone: profiles.timezone → browser
// Intl zone → America/Boise (resolveUserTimeZone; components use the
// useUserTimeZone() hook). All day-math takes `timeZone` (and an injectable
// `nowMs`) as parameters — the lib/followup.ts deterministic-test idiom.
//
// Pure module: no React, no supabase, no `@/` aliases — importable directly by
// the Node-24 `node --test` harness (tests/unit/dates.test.mjs).

export const FALLBACK_TIME_ZONE = 'America/Boise'

const DAY_MS = 86_400_000

export function isValidTimeZone(tz: string): boolean {
  if (!tz) return false
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/**
 * The zone every user-facing date renders in: a valid profiles.timezone wins,
 * else the browser's zone, else America/Boise (the brokerage's home zone —
 * also the server-side fallback used by the edge functions). browserTz is
 * injectable for tests; in the app it defaults to the live Intl resolution.
 */
export function resolveUserTimeZone(
  profileTz: string | null | undefined,
  browserTz: string | undefined = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  if (profileTz && isValidTimeZone(profileTz)) return profileTz
  if (browserTz && isValidTimeZone(browserTz)) return browserTz
  return FALLBACK_TIME_ZONE
}

/**
 * This device's zone when the browser reports a real one, else null (RIZ183). A new account
 * (handle_new_user, mig 479) and a new brokerage's texting hours and timed deadlines
 * (Onboarding) start in the zone they were set up from instead of America/Boise.
 */
export function deviceTimeZone(
  browserTz: string | undefined = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string | null {
  return browserTz && isValidTimeZone(browserTz) ? browserTz : null
}

export interface YMD { y: number; m: number; d: number }

/** Strict 'YYYY-MM-DD' → parts (hoisted from ImportantDatesCard); null otherwise. */
export function parseYMD(value: string): YMD | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null
}

/**
 * The calendar date a date-semantic value names: 'YYYY-MM-DD' by its parts,
 * anything else (date-semantic timestamptz) by its UTC calendar day — the same
 * contract formatDueDate has always rendered, so a pill's day-distance and the
 * printed date beside it can never disagree.
 */
export function dateSemanticYMD(value: string): YMD {
  const p = parseYMD(value)
  if (p) return p
  const d = new Date(value)
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() }
}

/** The calendar date of an instant in a zone (en-CA Intl parts — house idiom). */
export function zonedYMD(instantMs: number, timeZone: string): YMD {
  const s = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(instantMs))
  // en-CA formats as YYYY-MM-DD.
  const p = parseYMD(s)
  if (p) return p
  // Unreachable with a valid zone; defensive fallback to UTC parts.
  const d = new Date(instantMs)
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() }
}

/** "Today" as a calendar date in a zone. nowMs is injectable for tests. */
export function todayYMD(timeZone: string, nowMs: number = Date.now()): YMD {
  return zonedYMD(nowMs, timeZone)
}

/**
 * Whole calendar days from "today in `timeZone`" to a date-semantic value.
 * `< 0` is past its date and `0` is today. Which of those a deadline surface calls
 * "overdue" is lib/deadlineBands' rule (RIZ060: a deadline due today is not). Both operands are
 * UTC-midnight anchors, so the division is exact — DST days can't skew it.
 * One source of truth for the day-distance behind the ContactDeadlines pill, the /deadlines
 * bands and header counts, TransactionDetail's badge, useContactWarnings, and Tasks.
 */
export function daysUntil(value: string, timeZone: string, nowMs: number = Date.now()): number {
  return daysFromToday(value, todayYMD(timeZone, nowMs))
}

/**
 * daysUntil against a "today" the caller already has. Resolving today means building an
 * Intl formatter, which is nearly all of daysUntil's cost (measured ~25 µs a call against
 * ~0.2 µs for the arithmetic, so 20,000 rows take ~0.5 s one way and ~4 ms the other); a pass
 * over thousands of rows — the Tasks page's per-person counts — resolves it ONCE and calls
 * this per row. daysUntil is defined in terms of it, so there is still exactly one piece of
 * day-distance arithmetic.
 */
export function daysFromToday(value: string, today: YMD): number {
  const t = dateSemanticYMD(value)
  return Math.round((Date.UTC(t.y, t.m - 1, t.d) - Date.UTC(today.y, today.m - 1, today.d)) / DAY_MS)
}

/**
 * Days until the next annual occurrence of a stored 'YYYY-MM-DD' (birthday /
 * anniversary badge). Occurrence is built from the stored month/day parts in
 * the current year — Feb-29 rolls to Mar-1 in non-leap years, matching the
 * ImportantDatesCard convention this was hoisted from. null for unparseable.
 */
export function daysUntilNextAnnual(ymd: string, timeZone: string, nowMs: number = Date.now()): number | null {
  const p = parseYMD(ymd)
  if (!p) return null
  const today = todayYMD(timeZone, nowMs)
  const todayUtc = Date.UTC(today.y, today.m - 1, today.d)
  let occ = Date.UTC(today.y, p.m - 1, p.d)
  if (occ < todayUtc) occ = Date.UTC(today.y + 1, p.m - 1, p.d)
  return Math.round((occ - todayUtc) / DAY_MS)
}

/** Whole years owned as of the NEXT anniversary occurrence (>= 1 for a past close). */
export function anniversaryYearsAtNext(ymd: string, timeZone: string, nowMs: number = Date.now()): number | null {
  const p = parseYMD(ymd)
  if (!p) return null
  const today = todayYMD(timeZone, nowMs)
  const todayUtc = Date.UTC(today.y, today.m - 1, today.d)
  const occ = Date.UTC(today.y, p.m - 1, p.d)
  const occYear = occ < todayUtc ? today.y + 1 : today.y
  return occYear - p.y
}

/** A zone's UTC offset (ms) at a given instant. */
export function tzOffsetMs(timeZone: string, utcMs: number): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(utcMs))
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0)
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
  return asUtc - Math.floor(utcMs / 1000) * 1000
}

/**
 * The instant of the earliest moment of a calendar date in a zone (two-pass
 * fixpoint, date-fns-tz style). The 8 zones Profile offers all shift at 02:00,
 * so wall-clock midnight exists; but in zones that spring forward AT 00:00
 * (e.g. America/Santiago, America/Havana) midnight doesn't exist and the
 * fixpoint lands at 23:00 the PREVIOUS day — so after the fixpoint we step
 * forward to the first instant that actually falls on the target date (the
 * transition point, e.g. 01:00 local). Normal zones return the fixpoint unchanged.
 */
export function zonedMidnightUtcMs(ymd: YMD, timeZone: string): number {
  const naive = Date.UTC(ymd.y, ymd.m - 1, ymd.d)
  const guess = naive - tzOffsetMs(timeZone, naive)
  let candidate = naive - tzOffsetMs(timeZone, guess)
  const zonedUtcDay = (ms: number): number => {
    const p = zonedYMD(ms, timeZone)
    return Date.UTC(p.y, p.m - 1, p.d)
  }
  // Step forward past a skipped midnight (DST gaps are 1h; cap the scan at 6h).
  for (let i = 0; i < 6 && zonedUtcDay(candidate) < naive; i++) {
    candidate += 3600_000
  }
  return candidate
}

// ── the showings business clock (G2 audit BLOCKER 2 + MAJOR F) ──────────────

/**
 * The property/business clock for showings.scheduled_at, END TO END: the
 * datetime-local write converts through it, all four scheduled_at renderers
 * (ShowingsCard, /showings, the portal feedback form + seller tracker via the
 * portal/src/format.ts mirror) format in it, and the dispatch email
 * (_shared/showing-dispatch.ts showingAtLabel) already pins the same literal —
 * tests/unit/showing-timezone-parity.test.mjs proves all three runtimes agree.
 *
 * DELIBERATELY a distinct constant from FALLBACK_TIME_ZONE even though equal
 * today: that one is the per-viewer DISPLAY fallback in resolveUserTimeZone,
 * and sharing it would let a viewer-display edit silently move the business
 * anchor. A showing is a fact about the property's clock, viewer-independent —
 * before this anchor, a non-Mountain browser wrote the WRONG INSTANT (its own
 * reading of the wall-clock digits) and four surfaces rendered a different
 * calendar day than the email.
 *
 * When multi-market ships (WS2 direction a), this becomes a resolved
 * organizations.timezone — every zone-sensitive helper below already takes
 * timeZone as an explicit parameter, so only the SOURCE of this string moves;
 * that is a one-column additive migration, not a rework.
 */
export const SHOWING_TIME_ZONE = 'America/Boise'

export interface YMDHM extends YMD { h: number; min: number }

/**
 * Strict 'YYYY-MM-DDTHH:mm[:ss]' (the datetime-local grammar; seconds
 * ignored) → parts, null otherwise. Validity is checked ZONE-FREE via a
 * Date.UTC round-trip on the parts (the partsToISO idiom) — never
 * `new Date(y, m-1, …)`, which is host-zone-dependent.
 */
export function parseDateTimeLocal(value: string): YMDHM | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/.exec(value)
  if (!m) return null
  const [, y, mo, d, h, min] = m.map(Number)
  const dt = new Date(Date.UTC(y, mo - 1, d, h, min))
  if (
    dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d ||
    dt.getUTCHours() !== h || dt.getUTCMinutes() !== min
  ) return null
  return { y, m: mo, d, h, min }
}

/**
 * The instant a wall-clock reading names in a zone — zonedMidnightUtcMs's
 * two-pass fixpoint generalized off midnight. The gap-step guard compares the
 * candidate's WALL-CLOCK VALUE (`candidate + tzOffsetMs(...)`) to the naive
 * wall clock, NOT the calendar day: the day-only form is exact at midnight but
 * measured up to 6h wrong for afternoon/evening readings. A non-existent
 * spring-forward reading shifts forward by the gap, minutes preserved (02:30
 * → 03:30 local); an ambiguous fall-back reading resolves to its FIRST
 * occurrence (the earlier-offset convention, deterministic); every other
 * reading returns the fixpoint unchanged.
 */
export function zonedWallClockUtcMs(parts: YMDHM, timeZone: string): number {
  const naive = Date.UTC(parts.y, parts.m - 1, parts.d, parts.h, parts.min)
  const guess = naive - tzOffsetMs(timeZone, naive)
  let candidate = naive - tzOffsetMs(timeZone, guess)
  for (let i = 0; i < 6 && candidate + tzOffsetMs(timeZone, candidate) < naive; i++) {
    candidate += 3600_000
  }
  return candidate
}

/**
 * THE showing timestamp renderer — Boise-anchored zone, the viewer's own
 * locale (the bug was the timezone, never the locale). Mirrored byte-identical
 * in portal/src/format.ts (separate bundle; parity-pinned). '' for nullish or
 * unparseable input.
 */
export function formatShowingAt(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, {
        timeZone: SHOWING_TIME_ZONE,
        weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
}

/**
 * "Now" as a datetime-local input value IN A ZONE — the Log-showing dialog's
 * seed. The old seed used the browser's wall clock, which pre-filled a
 * non-Mountain device with its own local time (the other half of the wrong-
 * instant write).
 */
export function nowInZoneInputValue(timeZone: string, nowMs: number = Date.now()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(nowMs))
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}

/** ISO instant of the start of "today" in a zone — for .gte('created_at', …) bounds. */
export function startOfTodayUtcIso(timeZone: string, nowMs: number = Date.now()): string {
  return new Date(zonedMidnightUtcMs(todayYMD(timeZone, nowMs), timeZone)).toISOString()
}

/** ISO instant of the start of the current calendar month in a zone. */
export function startOfMonthUtcIso(timeZone: string, nowMs: number = Date.now()): string {
  const today = todayYMD(timeZone, nowMs)
  return new Date(zonedMidnightUtcMs({ y: today.y, m: today.m, d: 1 }, timeZone)).toISOString()
}

/**
 * Render a date-semantic value as its stored calendar date — TZ-free by
 * construction (parts → local-anchored Date → toLocaleDateString), so it can
 * never shift a day in any zone (the s6 `new Date('YYYY-MM-DD')` trap).
 * Unparseable input echoes back raw (cma fmtDate precedent).
 */
export function formatDateOnly(value: string, opts?: Intl.DateTimeFormatOptions): string {
  let p = parseYMD(value)
  if (!p) {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    // Date-semantic timestamptz: its UTC calendar day (formatDueDate contract).
    p = { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() }
  }
  return new Date(p.y, p.m - 1, p.d).toLocaleDateString(undefined, opts)
}

/**
 * Render a due date stored as a date-semantic value (moved from lib/utils).
 * Both task writers (deepgram-callback auto-tasks and CreateTaskDialog) store
 * plain dates, which Postgres records as UTC midnight — rendering those as
 * local instants shifts the day west of UTC, so show the stored calendar date.
 * Output-identical to the old UTC-calendar render (unit-pinned).
 */
export function formatDueDate(value: string): string {
  return formatDateOnly(value)
}

/** Render an instant's date in the user's resolved zone. */
export function formatInstantDate(iso: string, timeZone: string, opts?: Intl.DateTimeFormatOptions): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { timeZone, ...opts })
}

/** Render an instant's date + time in the user's resolved zone. */
export function formatInstantDateTime(iso: string, timeZone: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, { timeZone })
}

/** Render an instant's wall time in the user's resolved zone. */
export function formatInstantTime(iso: string, timeZone: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleTimeString(undefined, { timeZone, hour: 'numeric', minute: '2-digit' })
}

/**
 * Relative "x ago" label for an instant. Hoisted from ContactActivity (R3-1),
 * which kept it at module scope because it reads the clock — the definition
 * must stay out of component render paths (react-hooks/purity); call sites in
 * render are fine. Shared by ContactActivity and NotesCard. Elapsed branches
 * are instant math (TZ-free); past a week it falls back to the absolute date
 * in the user's resolved zone (PR-2). nowMs is injectable — the module's
 * deterministic-test idiom.
 */
export function timeAgo(dateStr: string, tz: string, nowMs: number = Date.now()): string {
  const diff = Math.floor((nowMs - new Date(dateStr).getTime()) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return formatInstantDate(dateStr, tz)
}

// ---------------------------------------------------------------------------
// Mirrors of supabase/functions/_shared/dates.ts (sanitizeDate +
// normalizeDateString) — Deno-side code the Vite bundle does not import
// (tsconfig.app.json includes only src; the src/lib/autonomy.ts mirror
// precedent). Maintained copies, pinned identical by
// tests/unit/normalize-date-parity.test.mjs. Edit BOTH files together.
// Added for F-11 (G1 audit): EditDeadlinesDialog seeds legacy human-format
// payload dates through the SAME explicit tables the F1 resolver uses —
// never locale Date parsing, never slice(0,10).

export function sanitizeDate(val: unknown): string | null {
  if (!val || typeof val !== 'string') return null
  const match = val.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const [, y, m, d] = match.map(Number)
  const date = new Date(y, m - 1, d)
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null
  return val
}

const MONTH_NAMES: Record<string, number> = {
  january: 1, jan: 1,
  february: 2, feb: 2,
  march: 3, mar: 3,
  april: 4, apr: 4,
  may: 5,
  june: 6, jun: 6,
  july: 7, jul: 7,
  august: 8, aug: 8,
  september: 9, sept: 9, sep: 9,
  october: 10, oct: 10,
  november: 11, nov: 11,
  december: 12, dec: 12,
}

function partsToISO(y: number, m: number, d: number): string | null {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null
  const dt = new Date(Date.UTC(y, m - 1, d))
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

export function normalizeDateString(val: unknown): string | null {
  if (!val || typeof val !== 'string') return null
  const s = val.trim()
  const strict = sanitizeDate(s)
  if (strict) return strict
  // ISO timestamp ("2026-07-11T00:00:00Z", "2026-07-11 00:00:00") → date part.
  if (/^\d{4}-\d{2}-\d{2}[T ]/.test(s)) return sanitizeDate(s.slice(0, 10))
  // "July 11, 2026" / "Jul. 11, 2026" / "July 11th, 2026" / "July 11 2026".
  const named = s.match(/^([A-Za-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?\s*,?\s+(\d{4})$/)
  if (named) {
    const month = MONTH_NAMES[named[1].toLowerCase()]
    if (!month) return null
    return partsToISO(Number(named[3]), month, Number(named[2]))
  }
  // "07/11/2026" / "7/4/2026" (MDY).
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (mdy) return partsToISO(Number(mdy[3]), Number(mdy[1]), Number(mdy[2]))
  return null
}
